import html2pdf from 'html2pdf.js/dist/html2pdf.bundle.min.js';
import { observer } from 'mobx-react';
import React from 'react';

// Translation
import translate from '../../translate/Translate';

// Helpers
import { getUrlSegment } from '../../../helpers';

// Icons
import * as icons from '../../ui/Icons';

// Components
import Button from '../../common/Button';
import Dropdown from '../../common/Dropdown';
import Pagination from '../../common/Pagination';
import ActivityHistory from './ActivityHistory';
import Appendix from './Appendix';
import Page from './Page';

const PLGReport = observer(
  class PLGReport extends React.Component {
    constructor() {
      super();

      // Set the initial state
      this.state = {
        activePageIndex: 0,
        attempts: null,
        downloadError: false,
        generatingPdf: false,
        navigation: [],
        showDownloadDialog: false
      };

      this.downloadButton = React.createRef();
      this.downloadDialog = React.createRef();
      this.downloadDialogClose = React.createRef();
      this.pdfReport = React.createRef();
    }

    componentDidMount() {
      const { AssessmentStore, ReportStore, UserStore } = this.props.store;
      const { targetUserData, userData } = UserStore;
      const { role, userSid } = userData;

      const query = {
        assessmentGroupSid: UserStore.assessmentGroupSid,
        role
      };

      if(role?.toLowerCase() === 'learner') {
        query.userSid = userSid;
        this.getUserAttempts(userSid, 'all-complete');
      } else {
        query.userSid = targetUserData.userSid;
        this.getUserAttempts(targetUserData.userSid, 'all-complete');
      }

      ReportStore.getReportData(query, this.createNavigation);

      if (AssessmentStore.assessments.length === 0) {
        AssessmentStore.listGroupAssessments(UserStore.assessmentGroupSid, {
          userSid: UserStore.userData.userSid
        });
      }
    }

    componentDidUpdate(prevProps) {
      if (prevProps.location.pathname !== this.props.location.pathname) {
        this.setActivePage();
      }
    }

    getUserAttempts = (userSid, completionState) => {
      const { AssessmentStore, UserStore } = this.props.store;

      AssessmentStore.listAttempts({
        userSid,
        completionState,
        assessmentGroupSid: UserStore.assessmentGroupSid
      }, this.formatAttempts);
    }

    formatAttempts = attempts => {
      const attemptObj = {};

      attempts.forEach(attempt => {
        if(!attemptObj[attempt.assessmentSid]) {
          attemptObj[attempt.assessmentSid] = {
            count: 1
          };
        } else {
          attemptObj[attempt.assessmentSid].count += 1;
        }

        if(attempt.mostRecentCompleted) {
          attemptObj[attempt.assessmentSid].lastCompletedDate = attempt.dateCompleted;
        }
      });

      this.setState({
        attempts: attemptObj
      });
    }

    createNavigation = data => {
      const { locale } = this.props.store.AppStore;
      const { role } = this.props.store.UserStore.userData;
      const navigation = [];

      data.forEach(page => {
        const title = page.title[locale];

        navigation.push({
          title,
          url: title.toLowerCase().replace(/ /g, '-')
        });
      });

      if (role === 'administrator' || role === 'instructor') {
        navigation.push({
          title: 'Activity History',
          url: 'activity-history'
        })
      }

      this.setState(
        {
          navigation
        },
        this.setActivePage
      );
    };

    setActivePage = () => {
      let { activePageIndex } = this.state;
      const navigation = [...this.state.navigation];
      const segment = getUrlSegment(2);

      if (segment) {
        for (let i = 0; i < navigation.length; i++) {
          if (navigation[i].url === segment) {
            navigation[i].active = true;
            activePageIndex = i;
          } else {
            navigation[i].active = false;
          }
        }

        this.setState({
          activePageIndex,
          navigation
        });
      }
    };

    resourceClicked = (link, reportSid) => {
      const { ActivityStore, AppStore } = this.props.store;
      const { locale } = AppStore;

      ActivityStore.sendResourceLinkClickedActivity({
        url: link.url,
        label: link.label[locale],
        reportSid
      });
    };

    openDownloadDialog = () => {
      this.setState({ downloadError: false, showDownloadDialog: true }, () => {
        this.downloadDialogClose.current.focus();
      });
    };

    closeDownloadDialog = () => {
      this.setState({ showDownloadDialog: false }, () => {
        this.downloadButton.current.focus();
      });
    };

    getPdfFilename = () => {
      const { signOnData, userData } = this.props.store.UserStore;
      const firstName = signOnData?.data?.lisPersonNameGiven ||
        userData.firstName ||
        signOnData?.data?.lisPersonNameFull?.trim().split(/\s+/)[0] ||
        'Student';
      const safeFirstName = firstName.replace(/[<>:"/\\|?*]/g, '').trim() || 'Student';
      const date = new Date();
      const formattedDate = [
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
        date.getFullYear()
      ].join('-');

      return `${safeFirstName} - Personal Learning Guide - ${formattedDate}.pdf`;
    };

    waitForPdfAssets = async () => {
      if (document.fonts && document.fonts.ready) await document.fonts.ready;

      const images = Array.from(this.pdfReport.current.querySelectorAll('img'));
      await Promise.all(images.map(image => {
        if (image.complete) {
          return image.naturalWidth ? Promise.resolve() : Promise.reject(new Error('Report image failed to load'));
        }

        return new Promise((resolve, reject) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', reject, { once: true });
        });
      }));

      await new Promise(resolve => setTimeout(resolve, 200));
    };

    downloadReport = () => {
      this.setState({
        downloadError: false,
        generatingPdf: true,
        showDownloadDialog: false
      }, async () => {
        this.downloadButton.current.focus();

        try {
          await this.waitForPdfAssets();
          await html2pdf()
            .set({
              filename: this.getPdfFilename(),
              image: { type: 'jpeg', quality: 0.75 },
              html2canvas: { logging: false, scale: 1.5, useCORS: true },
              jsPDF: { compress: true, format: 'letter', orientation: 'portrait', unit: 'in' },
              margin: 0.5,
              pagebreak: { mode: ['css', 'legacy'], avoid: ['h1', 'h2', 'h3', 'p', 'li', '.page__heading', '.page__header', '.page__section'] }
            })
            .from(this.pdfReport.current)
            .save();

          this.setState({ generatingPdf: false });
        } catch {
          this.setState({
            downloadError: true,
            generatingPdf: false,
            showDownloadDialog: true
          }, () => this.downloadDialogClose.current.focus());
        }
      });
    };

    handleDownloadDialogKeyDown = e => {
      if (e.key === 'Escape') {
        this.closeDownloadDialog();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusableElements = this.downloadDialog.current.querySelectorAll('button');
      const firstItem = focusableElements[0];
      const lastItem = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };

    renderPageType = page => {
      const { attempts } = this.state;
      const { role } = this.props.store.UserStore.userData;

      switch (getUrlSegment(2)) {
        case 'appendix':
          return (
            <Appendix
              {...this.props}
              page={page}
              handleResource={this.resourceClicked}
            />
          );

        case 'activity-history':
          if (role === 'instructor' || role === 'administrator') {
            return (
              <ActivityHistory {...this.props} />
            )
          } else {
            return null;
          }

        default:
          return (
            <Page
              {...this.props}
              attemptCount={attempts[page.assessmentSid]?.count || null}
              handleResource={this.resourceClicked}
              lastCompletedDate={attempts[page.assessmentSid]?.lastCompletedDate || null}
              page={page}
            />
          );
      }
    };

    render() {
      const { activePageIndex, attempts, downloadError, generatingPdf, navigation } = this.state;
      const { store, translation } = this.props;
      const { AppStore, ReportStore, UserStore } = store;
      const { reportData } = ReportStore;
      const { signOnData, targetUserData, targetUserId, userData } = UserStore;
      const { lmsId, role } = userData;

      if (!AppStore.loading && reportData) {
        return (
          <React.Fragment>
            <section className="report__head">
              <div className="wrap">
                <article className="report__target" aria-labelledby="learner-info-heading">
                  <h3 id="learner-info-heading" className="meta">{translation.learner_info}</h3>
                  {/* Use target user data for non-learners */}
                  {role.toLowerCase() !== 'learner' && targetUserData &&
                    <div>
                      <p>
                        <strong>{`${targetUserData.firstName} ${targetUserData.lastName}`}</strong>
                      </p>
                      {targetUserId &&
                        <p>
                          <span>Student ID: {targetUserId}</span>
                        </p>
                      }
                    </div>
                  }

                  {/* Use sign on data for learners */}
                  {role.toLowerCase() === 'learner' && signOnData &&
                    <div>
                      <p>
                        <strong>{signOnData.data.lisPersonNameFull}</strong>
                      </p>

                      {lmsId &&
                        <p>
                          <span>Student ID: {lmsId}</span>
                        </p>
                      }
                    </div>
                  }
                </article>

                <nav className="report__nav" aria-labelledby="report-nav">
                  <h2 className="meta" id="report-nav">Report navigation</h2>
                  <Dropdown
                    {...this.props}
                    className="icon--sm report__nav-dropdown"
                    icon={<icons.list />}
                    id="report-navlist"
                    onSelect={activePageIndex => this.setState({ activePageIndex })}
                    options={navigation}
                    triggerText={navigation[activePageIndex]?.title}
                  />
                </nav>

                <div className="report__actions">
                  {role.toLowerCase() === 'learner' && (
                    <Button
                      ariaDisabled={generatingPdf}
                      ariaLabel={generatingPdf ? translation.download.generating : translation.download.open}
                      className="btn report__download-btn"
                      disabled={generatingPdf}
                      onClick={this.openDownloadDialog}
                      refValue={this.downloadButton}
                      type="button"
                    >
                      <icons.download />
                      {generatingPdf ? translation.download.generating : translation.download.open}
                    </Button>
                  )}

                  <Pagination
                    {...this.props}
                    activePageIndex={activePageIndex}
                    className="report__pagination"
                    nextText={translation.pagination.next}
                    nextUrl={navigation[activePageIndex + 1]?.url}
                    onClick={activePageIndex => this.setState({ activePageIndex })}
                    previousText={translation.pagination.previous}
                    previousUrl={navigation[activePageIndex - 1]?.url}
                    title={translation.pagination.title}
                    hideText
                  />
                </div>
              </div>
            </section>

            <main
              id="main-content"
              className="page"
              tabIndex="-1"
              aria-labelledby="page-title"
            >
              {/* Render static or dynamic pages */}
              {this.renderPageType(reportData[activePageIndex])}
            </main>

            {generatingPdf && (
              <div aria-hidden="true" className="report-pdf__wrapper">
                <div className="report-pdf" ref={this.pdfReport}>
                  {reportData.map((page, index) => (
                    <article className="report-pdf__page" key={`${page.reportSid}-${index}`}>
                      {navigation[index]?.url === 'appendix' ? (
                        <Appendix
                          {...this.props}
                          handleResource={this.resourceClicked}
                          page={page}
                          pdfMode
                        />
                      ) : (
                        <Page
                          {...this.props}
                          attemptCount={attempts?.[page.assessmentSid]?.count || null}
                          handleResource={this.resourceClicked}
                          lastCompletedDate={attempts?.[page.assessmentSid]?.lastCompletedDate || null}
                          page={page}
                          pdfMode
                        />
                      )}
                    </article>
                  ))}
                </div>
              </div>
            )}

            {this.state.showDownloadDialog && (
              <div className="dialog__overlay report-download__overlay">
                <aside
                  aria-describedby="download-report-description"
                  aria-labelledby="download-report-heading"
                  aria-modal="true"
                  className="dialog report-download"
                  onKeyDown={this.handleDownloadDialogKeyDown}
                  ref={this.downloadDialog}
                  role="dialog"
                >
                  <div className="dialog__content report-download__content">
                    <div className="dialog__title report-download__title">
                      <h2 className="dialog__heading" id="download-report-heading">
                        <icons.download />
                        {translation.download.heading}
                      </h2>
                      <Button
                        ariaLabel={translation.download.close}
                        className="dialog__close"
                        onClick={this.closeDownloadDialog}
                        refValue={this.downloadDialogClose}
                        type="button"
                      >
                        <icons.times />
                      </Button>
                    </div>

                    <div id="download-report-description">
                      <p><strong>{translation.download.description}</strong></p>
                      <p>{translation.download.sharing}</p>
                      <p>{translation.download.instructions}</p>
                    </div>

                    {downloadError && (
                      <p className="report-download__error" role="alert">
                        {translation.download.error}
                      </p>
                    )}

                    <div className="dialog__action report-download__actions">
                      <Button
                        className="btn btn--outline"
                        onClick={this.closeDownloadDialog}
                        type="button"
                      >
                        {translation.download.cancel}
                      </Button>
                      <Button
                        className="btn"
                        onClick={this.downloadReport}
                        type="button"
                      >
                        <icons.download />
                        {translation.download.download}
                      </Button>
                    </div>
                  </div>
                </aside>
              </div>
            )}
          </React.Fragment>
        );
      }

      return null;
    }
  }
);

export default translate('PLGReport')(PLGReport);
